import React, {FC, useEffect, useState} from "react"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import {H3, H4, Body} from "@irati/ui/src/Typography"
import wellnessService from "@irati/wellness/services/Wellness"
import tracking from "@irati-tracking/wellness"
import {
  CategoriesWrapper
} from "@irati-web/perks/src/components/CustomCategories/styled"
import {Grid} from "react-bootstrap"
import {Col, Row} from "@irati/ui/src/ColumnRow"
import styled, {css} from "styled-components"
import {RouteComponentProps} from "react-router-dom"
import WellnessMediaListComponent, {getMediaList} from "@irati/wellness/apollo/query/WellnessMediaList"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import Selector from "@irati/ui/src/Selector"
import ShowMoreOrThatsAll from "@irati-web/perks/src/components/DealList/ShowMoreOrThatsAll"
import MediaCard from "../components/MediaCard"
import WellnessCategoryList from "../components/Category/WellnessCategoryList"

const INITIAL_VARIABLES = {limit: 9, offset: 0}

interface Match {
  categoryId?: string
}

export const TagSelector = styled(Selector)`
  ${({theme, selected}) => css`
    background-color: ${selected ? theme.colors.primary2 : theme.colors.utility.white};
    ${theme.mediaQueries.maxWidthMedia.tablet`
      width: initial;
    `}
  `}
`

interface Props extends InjectIntl, RouteComponentProps<Match> {
}

const BrowseByCategory: FC<Props> = (props) => {

  const [categoryId, setCategoryId] = useState<string>(props.match.params.categoryId)
  const idNamespace = id("wellness-category-list", props)
  const [tags, setTags] = useState<String[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [variables] = useState(INITIAL_VARIABLES)
  const [isActive, setActive] = useState(true)
  const [categoryName, setCategoryName] = useState<string>("")

  useEffect(() => {
    wellnessService.getWellnessCategoryById(categoryId).then((category) => setCategoryName(category.name))
    getWellnessTags(categoryId)

  }, [categoryId])

  const selectCategory = (uuid: string, name: string) => {
    if (categoryId === uuid) {
      setActive(false)
    } else {
      setActive(true)
      setCategoryId(uuid)
    }
    setSelectedTags([])

    tracking.viewCategorySelected(name)
    getWellnessTags(uuid)
  }

  const getWellnessTags = (catId: string) => {
    wellnessService.getWellnessTagsByCategoryId(catId).then((categoryTags: String[]) => {
      setTags(categoryTags)
    })
  }

  const tagSelection = (uuid: string, tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      tracking.viewCategoryTag(tagName)
      setSelectedTags([...selectedTags, tagName])
    } else {
      setSelectedTags(selectedTags.filter((element) => element !== tagName))
    }
  }

  const handleNewPage = (isVisible: boolean, apollo: ApolloState<any>) => {
    if (isVisible) {
      const {results, count} = getMediaList(apollo)
      const resultsLength = results?.length || 0
      if (resultsLength && resultsLength !== count) {
        return apollo.nextPage({...INITIAL_VARIABLES, offset: resultsLength, categoryId, tags: selectedTags})
      }
    }
  }

  return (
    <Grid>
      <WellnessCategoryList
        onClick={selectCategory}
        categoryId={categoryId}
        isActive={isActive} />

      {isActive && tags.length > 1 &&
      <>
        <H4 marginTop={2} marginBottom={2}>
          <FormattedMessage id="wellness.tags.title" />
        </H4>
        <CategoriesWrapper>
          {tags.map((name) =>
            <TagSelector
              key={name}
              name={name}
              onClick={tagSelection}
              selected={selectedTags.includes(name)} />
          )}
        </CategoriesWrapper>
      </>
      }

      <H3 marginTop={4}>
        {categoryName || <FormattedMessage id="wellness.allCategories" />}
      </H3>

      <Spacing marginLeft={-1} marginRight={-1}>
        <WellnessMediaListComponent variables={{...variables, categoryId, tags: selectedTags}}>
          {(apollo) => {
            const {results, count} = getMediaList(apollo)
            return (
              <>
                <Body marginBottom marginLeft>
                  <FormattedMessage id="wellness.categories.summary" values={{count, results: results?.length}} />
                </Body>
                <Row marginBottom={4}>
                  {results?.map((media, index) => (
                    <Col paddingLeft={0} paddingRight={0} id={id(`column-${index}`, idNamespace)} md={4} sm={6} xs={12}>
                      <Flex direction="column">
                        <MediaCard media={media} key={`media${index}`} />
                      </Flex>
                    </Col>
                  ))}
                </Row>
                <ShowMoreOrThatsAll onNewPage={handleNewPage} apollo={apollo} />
              </>
            )
          }}
        </WellnessMediaListComponent>
      </Spacing>
    </Grid>
  )
}

export default injectIntl(BrowseByCategory)
